import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  FinanceContactDto,
  FinanceContactListResponse,
  FinanceContactListParams,
  CreateFinanceContactRequest,
  UpdateFinanceContactRequest,
  FinanceCreateContactResponse,
  SetVoterLinkRequest,
  RecordDonorFollowUpRequest,
} from '@signalvote/shared';
import { DonorInfoRequestChannel } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse, apiVoid } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

// Re-export types for consumer convenience
export type {
  FinanceContactDto,
  FinanceContactListResponse,
  FinanceContactListParams,
  CreateFinanceContactRequest,
  UpdateFinanceContactRequest,
  FinanceCreateContactResponse,
};

// ============================================================================
// Query Hooks
// ============================================================================

/** Writes a contact's fresh token into its cached detail, so the next edit sends a current one. */
export function writeContactVersion(
  queryClient: QueryClient,
  contactId: string,
  concurrencyVersion: number,
) {
  queryClient.setQueryData<FinanceContactDto>(queryKeys.finance.contacts.detail(contactId), (old) =>
    old ? { ...old, concurrencyVersion } : old,
  );
}

function buildContactListUrl(params: FinanceContactListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.type != null) searchParams.set('type', params.type);
  if (params.search) searchParams.set('search', params.search);
  if (params.trafficStatus) searchParams.set('trafficStatus', params.trafficStatus);
  if (params.donorGiving) searchParams.set('donorGiving', params.donorGiving);
  return `/api/finance/contacts?${searchParams.toString()}`;
}

export function useFinanceContacts(params: FinanceContactListParams, options?: { enabled?: boolean }) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.contacts.list(params),
    queryFn: () =>
      apiJson<FinanceContactListResponse>(buildContactListUrl(params), {
        error: 'Failed to fetch finance contacts',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && (options?.enabled ?? true),
  });
}

export function useFinanceContact(id: string) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.contacts.detail(id),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/contacts/${id}`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch finance contact');
      return response.json() as Promise<FinanceContactDto>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!id,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateFinanceContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFinanceContactRequest) =>
      apiJson<FinanceCreateContactResponse>('/api/finance/contacts', {
        method: 'POST',
        json: data,
        error: (body) => new Error(body.error || 'Failed to create contact'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
      // The donor's gifts just gained a recorded ask each — an open triage panel showing
      // one of their histories would otherwise keep rendering the pre-follow-up list.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * USI-010 — typed error class for {@link useUpdateFinanceContact}.
 *
 * Pre-USI-010 the hook threw a plain `Error`. The triage-modal
 * MissingInfoBody (USI-010) needs to branch on the stable error CODE (not
 * HTTP status) per CONVENTIONS.md so a future 409 for a different reason
 * doesn't mis-render the refresh-prompt copy. Surfacing the response code
 * + status as a typed class keeps the branching readable without forcing
 * every caller to inspect raw HTTP envelope fields. Existing call sites
 * that only consume `err.message` continue to work — `FinanceContactUpdateError`
 * extends `Error`.
 *
 * Codes today: `"ConcurrencyConflict"` (409). The 400 / 404 paths still
 * surface as the error message string only; add more constants here if a
 * future UI branches on them.
 */
export class FinanceContactUpdateError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'FinanceContactUpdateError';
    this.code = code;
    this.status = status;
  }
}

export function useUpdateFinanceContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFinanceContactRequest }) =>
      apiJson<FinanceContactDto>(`/api/finance/contacts/${id}`, {
        method: 'PUT',
        json: data,
        error: (body, response) =>
          new FinanceContactUpdateError(
            body.code ?? '',
            response.status,
            body.error ?? 'Failed to update contact',
          ),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
      // USI-010: a contact update that fills in occupation/employer drops
      // the row from USI-007's sort-inbox missing-info queue. The
      // TriageModal's `handleClassify` also invalidates from the in-modal
      // path; double-invalidate is harmless (TanStack dedups) and keeps
      // non-triage callers (FinanceContact edit forms) correct
      // independently.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
  });
}

/**
 * Permanently dismiss the person-vs-org shape nudge on a contact whose shape the treasurer
 * verified is correct (POST /api/finance/contacts/{id}/confirm-shape). The endpoint enqueues a
 * ContributorShapeConfirmed audit row keyed on the contact; the derive-time sort-inbox read
 * suppresses BOTH shape nudges ("Looks like a PAC" / "Maybe a person — verify.") for every gift
 * from that donor. Idempotent server-side, so a double-dismiss is harmless. On success we
 * invalidate the sort inbox so the amber pill clears once the ledger refetches.
 */
export function useConfirmContributorShape() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['finance', 'contacts', 'confirm-shape'],
    // 204 No Content — no payload.
    mutationFn: ({ contactId }: { contactId: string }) =>
      apiVoid(`/api/finance/contacts/${contactId}/confirm-shape`, {
        method: 'POST',
        error: (body) => new Error(body.error || 'Failed to dismiss the shape reminder'),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
    },
  });
}

export function useLinkContactToVoter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      voterId,
      concurrencyVersion,
    }: {
      id: string;
      voterId: string | null;
      concurrencyVersion: number;
    }) => {
      // Voter-link is an optimistic-concurrency-guarded write on the versioned
      // FinanceContact: the backend rejects a stale token with a 409 and an omitted one
      // with a 400, so the current version must ride the body (mirrors useUpdateFinanceContact).
      const body: SetVoterLinkRequest = { voterId, concurrencyVersion };
      return apiJson<FinanceContactDto>(`/api/finance/contacts/${id}/voter-link`, {
        method: 'PUT',
        json: body,
        error: (errorBody) => new Error(errorBody.error || 'Failed to link voter'),
      });
    },
    onSuccess: () => {
      // This is the CONTACTS page, not the close ledger — no rowMutationScope /
      // reconcileSortInboxRowVersion recipe. Invalidating contacts.all refetches the
      // contact detail (which carries the freshly-bumped concurrencyVersion) so the next
      // link/unlink sends the new token, matching useUpdateFinanceContact's convention.
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
    },
  });
}

export function useMarkFollowUpSent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      channel,
      concurrencyVersion,
    }: {
      id: string;
      channel: DonorInfoRequestChannel;
      concurrencyVersion: number;
    }) => {
      return apiJson<FinanceContactDto>(`/api/finance/contacts/${id}/follow-up`, {
        method: 'PUT',
        json: { channel, concurrencyVersion } satisfies RecordDonorFollowUpRequest,
        error: (body) => new Error(body.error || 'Failed to record follow-up'),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.finance.contacts.all });
    },
  });
}
